/**
 * Sawyer E4 (#754) — who sees Team messages, and what they may do there.
 *
 *   visible   the role reaches /team-messages AND the server has the routes
 *             (AC17: no nav item, badge or poll before the server deploy)
 *   canWrite  staff-messages:write:all (every role but the accountant)
 *   canOpenDogs  pets:read:all — the dog chip links to the profile only for
 *             a staffer the server would let open it (AC5)
 */
import { useAuth } from '../../contexts/AuthContext.js';
import { useFeatureAvailability } from '../../contexts/FeatureAvailabilityContext.js';
import { effectiveRolesOf, rolesHavePermission } from '../../lib/rbac-matrix.js';
import {
  featureIsAvailable,
  rolesCanAccess,
  ROUTE_ACCESS_RULES,
  type FeatureAvailability,
} from '../../lib/route-permissions.js';

export interface TeamMessagesAccess {
  visible: boolean;
  canWrite: boolean;
  canOpenDogs: boolean;
  myId: string | null;
}

/** Pure form, for tests and for callers that already hold staff + features. */
export function teamMessagesAccess(
  staff: { id?: string; role?: string | null; roles?: readonly string[] | null } | null | undefined,
  features: FeatureAvailability | null | undefined,
): TeamMessagesAccess {
  const roles = effectiveRolesOf(staff);
  // platform_admin renders the operator console, which has no tenant board.
  const tenantRole = roles.length > 0 && !roles.includes('platform_admin');
  return {
    visible:
      tenantRole &&
      rolesCanAccess(roles, ROUTE_ACCESS_RULES['/team-messages']) &&
      featureIsAvailable('staffMessages', features),
    canWrite: tenantRole && rolesHavePermission(roles, 'staff-messages:write:all'),
    canOpenDogs: tenantRole && rolesHavePermission(roles, 'pets:read:all'),
    myId: staff?.id ?? null,
  };
}

export function useTeamMessagesAccess(): TeamMessagesAccess {
  const { staff } = useAuth();
  const { features } = useFeatureAvailability();
  return teamMessagesAccess(staff, features);
}
